import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useGetEngagementDashboard } from '@/api/generated/engagement/engagement';
import { selectData, type SuccessResponseDTO } from '@/api/envelope';
import { customInstance } from '@/api/http';
import type { EngagementDashboardResponseDTO, EngagementHeatmapDTO } from '@/api/generated/model';
import { apiCountryCode, geoRegionCode } from '@/lib/geoSelection';
import type { TimeRangeBounds } from '@/lib/timeRangeOptions';

export type { EngagementDashboardResponseDTO, EngagementHeatmapDTO };

function boundsFromRange(range: TimeRangeBounds | number | null | undefined): TimeRangeBounds {
  if (range == null || typeof range === 'number') {
    return range == null ? {} : { days: range };
  }
  return range;
}

function geoQueryParams(selection?: string): { countryCode?: string; regionCode?: string } {
  const country = apiCountryCode(selection ?? '');
  const region = geoRegionCode(selection ?? '');
  return {
    ...(country ? { countryCode: country } : {}),
    ...(region ? { regionCode: region } : {}),
  };
}

export function useEngagementDashboard(
  range: TimeRangeBounds | number | null | undefined,
  geoSelection?: string,
  includeHeatmap = false,
) {
  const bounds = boundsFromRange(range);

  return useGetEngagementDashboard(
    {
      ...(bounds.days != null ? { days: bounds.days } : {}),
      ...(bounds.startDate ? { startDate: bounds.startDate } : {}),
      ...(bounds.endDate ? { endDate: bounds.endDate } : {}),
      ...geoQueryParams(geoSelection),
      includeHeatmap,
    },
    {
      query: {
        select: selectData,
        placeholderData: keepPreviousData,
      },
    },
  );
}

/** GET /admin/api/engagement/heatmap — PostHog time-of-day only. */
export function useEngagementHeatmap(
  range: TimeRangeBounds | number | null | undefined,
  geoSelection?: string,
  timezone?: string,
) {
  const bounds = boundsFromRange(range);
  const params: Record<string, string | number> = { ...geoQueryParams(geoSelection) };
  if (bounds.days != null) params.days = bounds.days;
  if (bounds.startDate) params.startDate = bounds.startDate;
  if (bounds.endDate) params.endDate = bounds.endDate;
  if (timezone) params.timezone = timezone;

  return useQuery({
    queryKey: ['/admin/api/engagement/heatmap', params],
    queryFn: ({ signal }) =>
      customInstance<SuccessResponseDTO<EngagementHeatmapDTO>>({
        url: '/admin/api/engagement/heatmap',
        method: 'GET',
        params,
        signal,
      }),
    select: selectData,
    placeholderData: keepPreviousData,
  });
}
